import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../api/client";
import { useAuth } from "../context/AuthContext";
import Card from "../components/Card";

interface Evaluation {
  id: string;
  userId: string;
  evaluatorId: string;
  periodId: string;
  evaluationType: "SELF" | "MANAGER" | "PEER";
  status: "DRAFT" | "SUBMITTED" | "COMPLETED";
  scores: string; // JSON
  overallScore?: number;
  comments?: string;
  strengths?: string;
  developmentAreas?: string;
  submittedAt?: number;
  completedAt?: number;
  createdAt: number;
  updatedAt: number;
}

function EvaluationList() {
  const { user } = useAuth();
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<{
    status?: string;
    period?: string;
  }>({});

  // Fetch evaluations
  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.status) params.append("status", filter.status);
      if (filter.period) params.append("periodId", filter.period);

      const query = params.toString();
      const data = await apiClient.get<Evaluation[] | { evaluations: Evaluation[] }>(
        `/evaluations${query ? "?" + query : ""}`
      );
      const list = Array.isArray(data) ? data : data.evaluations || [];
      setEvaluations(list);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load evaluations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvaluations();
  }, [filter]);

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "DRAFT":
        return "bg-yellow-100 text-yellow-700";
      case "SUBMITTED":
        return "bg-blue-100 text-blue-700";
      case "COMPLETED":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // Get type badge color
  const getTypeColor = (type: string) => {
    switch (type) {
      case "MANAGER":
        return "bg-purple-100 text-purple-700";
      case "PEER":
        return "bg-orange-100 text-orange-700";
      case "SELF":
        return "bg-teal-100 text-teal-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading evaluations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">My Evaluations</h2>
        <p className="text-sm text-slate-500 mt-1">View and manage evaluations</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <Card title="Filters">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              value={filter.status || ""}
              onChange={(e) => setFilter({ ...filter, status: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Period</label>
            <input
              type="text"
              value={filter.period || ""}
              onChange={(e) => setFilter({ ...filter, period: e.target.value || undefined })}
              placeholder="Filter by period..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilter({})}
              className="w-full px-3 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      </Card>

      {/* Evaluations List */}
      <div className="space-y-4">
        {evaluations.length === 0 ? (
          <Card>
            <div className="text-center py-8 text-slate-500">
              No evaluations found. Create one to get started.
            </div>
          </Card>
        ) : (
          evaluations.map((evaluation) => (
            <Card
              key={evaluation.id}
              title={`Evaluation ${evaluation.id.substring(0, 8)}`}
              subtitle={`Period: ${evaluation.periodId}`}
              action={
                <Link
                  to={`/evaluations/${evaluation.id}/report`}
                  className="text-sm px-3 py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors"
                >
                  View Report
                </Link>
              }
            >
              <div className="space-y-4">
                {/* Status Row */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                          evaluation.status
                        )}`}
                      >
                        {evaluation.status}
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(
                          evaluation.evaluationType
                        )}`}
                      >
                        {evaluation.evaluationType}
                      </span>
                    </div>
                  </div>

                  {evaluation.overallScore !== undefined && evaluation.overallScore !== null && (
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Overall Score</p>
                      <p className="text-2xl font-bold text-indigo-600">
                        {evaluation.overallScore.toFixed(1)} / 5
                      </p>
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Employee ID</p>
                    <p className="font-mono text-slate-700">{evaluation.userId}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Evaluator ID</p>
                    <p className="font-mono text-slate-700">{evaluation.evaluatorId}</p>
                  </div>
                  {evaluation.comments && (
                    <div className="col-span-2">
                      <p className="text-slate-500">Comments</p>
                      <p className="text-slate-700 mt-1">{evaluation.comments}</p>
                    </div>
                  )}
                </div>

                {/* Dates */}
                <div className="grid md:grid-cols-2 gap-4 text-xs text-slate-500 pt-4 border-t border-slate-200">
                  <div>
                    <p>Created: {new Date(evaluation.createdAt).toLocaleDateString()}</p>
                  </div>
                  {evaluation.submittedAt && (
                    <div>
                      <p>Submitted: {new Date(evaluation.submittedAt).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

export default EvaluationList;
